export const SENSOR_STATUS = {
  ONLINE: 'online',
  DEGRADED: 'degraded',
  OFFLINE: 'offline',
  UNKNOWN: 'unknown',
} as const;

export const RECOMMENDATION_HEALTH = {
  CURRENT: 'current',
  STALE: 'stale',
  MISSING: 'missing',
  DISABLED: 'disabled',
} as const;

export const MONITORING_HEALTH = {
  ALL_GOOD: 'all_good',
  NEEDS_ATTENTION: 'needs_attention',
  CRITICAL: 'critical',
} as const;

export const NODE_TRANSPORT_TYPE = {
  CELLULAR: 'cellular',
  LORA: 'lora',
  UNKNOWN: 'unknown',
} as const;

export const NODE_SUBTYPE = {
  FIELD_NODE: 'field_node',
  DRAGINO_CELLULAR: 'dragino_cellular',
  MILESIGHT_CELLULAR: 'milesight_cellular',
  LORA_SENSOR_NODE: 'lora_sensor_node',
  LORA_WATERBIT: 'lora_waterbit',
  LORA_DRAGINO: 'lora_dragino',
} as const;

export type SensorStatus = typeof SENSOR_STATUS[keyof typeof SENSOR_STATUS];
export type RecommendationHealth = typeof RECOMMENDATION_HEALTH[
  keyof typeof RECOMMENDATION_HEALTH
];
export type MonitoringHealth = typeof MONITORING_HEALTH[keyof typeof MONITORING_HEALTH];
export type NodeTransportType = typeof NODE_TRANSPORT_TYPE[
  keyof typeof NODE_TRANSPORT_TYPE
];

const NODE_TRANSPORT_BY_SUBTYPE: Record<string, NodeTransportType> = {
  [NODE_SUBTYPE.FIELD_NODE]: NODE_TRANSPORT_TYPE.CELLULAR,
  [NODE_SUBTYPE.DRAGINO_CELLULAR]: NODE_TRANSPORT_TYPE.CELLULAR,
  [NODE_SUBTYPE.MILESIGHT_CELLULAR]: NODE_TRANSPORT_TYPE.CELLULAR,
  [NODE_SUBTYPE.LORA_SENSOR_NODE]: NODE_TRANSPORT_TYPE.LORA,
  [NODE_SUBTYPE.LORA_WATERBIT]: NODE_TRANSPORT_TYPE.LORA,
  [NODE_SUBTYPE.LORA_DRAGINO]: NODE_TRANSPORT_TYPE.LORA,
};

// Keep API status values stable while presenting clearer reporting-health labels to users.
export const SENSOR_HEALTH_LABELS: Record<SensorStatus, string> = {
  [SENSOR_STATUS.ONLINE]: 'Good',
  [SENSOR_STATUS.DEGRADED]: 'Fair',
  [SENSOR_STATUS.OFFLINE]: 'Poor',
  [SENSOR_STATUS.UNKNOWN]: 'Unknown',
};

export const RECOMMENDATION_STALE_HOURS = 48;
export const ENTITY_OFFLINE_CRITICAL_PERCENT = 50;

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const END_OF_DAY_UTC_SUFFIX = 'T23:59:59.999Z';

export interface BlockRecommendationStatus {
  blockId?: number;
  blockName?: string;
  lastRecommendationTime?: string | null;
  recommendationEnabled?: boolean;
  imageryRecommendationEnabled?: boolean;
  satelliteImageryEnabled?: boolean;
  satelliteSubscriptionActive?: boolean;
  latestSatelliteImageTime?: string | null;
}

export interface GatewaySensorLog {
  status?: string;
  type?: string;
  subtype?: string | null;
  nodeSubtype?: string | null;
  transportType?: string | null;
  gatewayCount?: number | null;
  sensorId?: number;
  sensorName?: string;
  sensorIdentifier?: string;
  blockId?: number;
  blockName?: string | null;
}

export interface GatewayLog {
  name?: string;
  identifier?: string;
  status?: string;
  isGateway?: boolean;
  sensors?: Record<string, GatewaySensorLog>;
  nodes?: Record<string, { status?: string; subtype?: string | null }>;
}

export interface DeviceSummary {
  totalGateways?: number;
  onlineGateways?: number;
  offlineGateways?: number;
  totalSensors?: number;
  onlineSensors?: number;
  degradedSensors?: number;
  offlineSensors?: number;
  totalNodes?: number;
  onlineNodes?: number;
  offlineNodes?: number;
}

export interface MonitoringIssue {
  type?: string;
  category?: string;
  priority?: number;
  severity?: string;
  deviceId?: number;
  deviceName?: string;
  deviceType?: string;
  sensorIdentifier?: string;
  gatewayIdentifier?: string | null;
  description?: string;
}

export interface MonitoringRow {
  id: number;
  ranchId?: number;
  ranchName?: string;
  entityId?: number;
  entityName?: string;
  date?: string;
  region?: string;
  deviceSummary?: DeviceSummary;
  deviceStatusLogs?: GatewayLog[];
  p0Issues?: MonitoringIssue[];
  p1Issues?: MonitoringIssue[];
  lastRecommendationTime?: string | null;
  blockRecommendationStatus?: BlockRecommendationStatus[];
  subscriptionType?: string | null;
  irrigationRecommendationsEnabled?: boolean;
  imageryRecommendationsEnabled?: boolean;
  satelliteImageryActive?: boolean;
}

export interface MonitoringSensorRow {
  id: string;
  sensorKey: string;
  sensorId?: number;
  sensorIdentifier: string;
  name: string;
  sensorType: string;
  entityName: string;
  ranchName: string;
  gatewayName: string;
  gatewayCount?: number | null;
  nodeSubtype?: string | null;
  transportType: NodeTransportType;
  blockId?: number;
  blockName?: string | null;
  status: SensorStatus;
}

export interface MonitoringGatewayRow {
  id: string;
  name: string;
  entityName: string;
  ranchName: string;
  connectedSensors: number;
  connectedNodes: number;
  status: SensorStatus;
}

export interface MonitoringBlockRow {
  id: string;
  blockId?: number;
  name: string;
  ranchName: string;
  entityName: string;
  lastRecommendationTime: string | null;
  recommendationHealth: RecommendationHealth;
  recommendationAgeHours: number | null;
  recommendationEnabled?: boolean;
  imageryRecommendationEnabled?: boolean;
  satelliteImageryEnabled?: boolean;
}

export interface MonitoringRanchRow {
  id: number | string;
  name: string;
  entityName: string;
  blocks: number;
  sensors: number;
  online: number;
  degraded: number;
  offline: number;
  gateways: number;
  onlineGateways: number;
  offlineGateways: number;
  gatewayApplicable?: boolean;
  recommendationCurrent: number;
  recommendationStale: number;
  recommendationMissing: number;
  health: MonitoringHealth;
}

export interface MonitoringEntityRow extends MonitoringRanchRow {
  id: string;
  ranches: number;
}

export interface EntityDrawerBlock extends MonitoringBlockRow {
  sensors: MonitoringSensorRow[];
}

export interface EntityDrawerRanch {
  id: number;
  name: string;
  sensors: number;
  blocks: EntityDrawerBlock[];
  unassignedSensors: MonitoringSensorRow[];
}

export interface EntityDrawerData {
  id: string;
  name: string;
  subscriptionType?: string | null;
  irrigationRecommendationsEnabled?: boolean;
  imageryRecommendationsEnabled?: boolean;
  satelliteImageryActive?: boolean;
  ranches: EntityDrawerRanch[];
}

const STATUS_ORDER: Record<string, number> = {
  [SENSOR_STATUS.ONLINE]: 1,
  [SENSOR_STATUS.DEGRADED]: 2,
  [SENSOR_STATUS.OFFLINE]: 3,
};

const SENSOR_ISSUE_STATUS_MAP: Record<string, SensorStatus> = {
  sensor_offline: SENSOR_STATUS.OFFLINE,
  sensor_degraded: SENSOR_STATUS.DEGRADED,
};

const SUPPORTED_SENSOR_STATUSES: string[] = [
  SENSOR_STATUS.ONLINE,
  SENSOR_STATUS.DEGRADED,
  SENSOR_STATUS.OFFLINE,
];

export const formatLabel = (value?: string | null) => {
  if (!value) return '-';
  return value
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const normalizeColumnOrder = (savedOrder: string[] | undefined, defaultOrder: string[]) => {
  const validSavedOrder = Array.isArray(savedOrder)
    ? savedOrder.filter((key, index) => (
      defaultOrder.includes(key) && savedOrder.indexOf(key) === index
    ))
    : [];
  return [
    ...validSavedOrder,
    ...defaultOrder.filter((key) => !validSavedOrder.includes(key)),
  ];
};

export const moveColumn = (order: string[], sourceKey: string, targetKey: string) => {
  const sourceIndex = order.indexOf(sourceKey);
  const targetIndex = order.indexOf(targetKey);
  if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) return order;

  const nextOrder = [...order];
  const [movedKey] = nextOrder.splice(sourceIndex, 1);
  nextOrder.splice(targetIndex, 0, movedKey);
  return nextOrder;
};

const sortByName = <T extends { name: string }>(rows: T[]) => [...rows].sort(
  (a, b) => String(a.name || '').localeCompare(String(b.name || '')),
);

const getIssueSensorKey = (issue: MonitoringIssue) => String(
  issue.sensorIdentifier
  || issue.deviceId
  || issue.deviceName
  || issue.description
  || 'unknown-sensor',
);

const normalizeSensorStatus = (status?: string): SensorStatus => {
  if (status && SUPPORTED_SENSOR_STATUSES.includes(status)) return status as SensorStatus;
  return SENSOR_STATUS.UNKNOWN;
};

export const getNodeTransportType = (
  transportType?: string | null,
  nodeSubtype?: string | null,
): NodeTransportType => {
  if (transportType === NODE_TRANSPORT_TYPE.CELLULAR) return NODE_TRANSPORT_TYPE.CELLULAR;
  if (transportType === NODE_TRANSPORT_TYPE.LORA) return NODE_TRANSPORT_TYPE.LORA;
  return NODE_TRANSPORT_BY_SUBTYPE[nodeSubtype || ''] || NODE_TRANSPORT_TYPE.UNKNOWN;
};

type GatewayDisplayFields = Pick<MonitoringSensorRow,
'transportType' | 'nodeSubtype' | 'gatewayCount' | 'gatewayName'>;

export const getGatewayDisplayValue = ({
  transportType,
  nodeSubtype,
  gatewayCount,
  gatewayName,
}: GatewayDisplayFields) => {
  const resolvedTransport = getNodeTransportType(transportType, nodeSubtype);
  if (resolvedTransport === NODE_TRANSPORT_TYPE.CELLULAR) return 'N/A';
  if (resolvedTransport === NODE_TRANSPORT_TYPE.LORA) {
    if (typeof gatewayCount === 'number') return String(gatewayCount);
    return gatewayName && gatewayName !== '-' ? '1' : '0';
  }
  return gatewayName || '-';
};

const buildLoggedSensorRows = (row: MonitoringRow): MonitoringSensorRow[] => {
  const sensors: MonitoringSensorRow[] = [];

  row.deviceStatusLogs?.forEach((gateway) => {
    Object.entries(gateway.sensors || {}).forEach(([identifier, sensor]) => {
      const sensorKey = String(identifier || `${gateway.identifier || gateway.name || row.id}-sensor`);
      const sensorIdentifier = sensor.sensorIdentifier || identifier || sensorKey;
      const gatewayName = gateway.name || gateway.identifier || '-';
      let { gatewayCount } = sensor;
      if (gatewayCount === undefined && gateway.isGateway === false) gatewayCount = 0;
      sensors.push({
        id: `${row.ranchId || row.id}-${sensorKey}`,
        sensorKey,
        sensorId: sensor.sensorId,
        sensorIdentifier,
        name: sensor.sensorName || sensorIdentifier,
        sensorType: formatLabel(sensor.type || sensor.subtype || 'sensor'),
        entityName: row.entityName || '-',
        ranchName: row.ranchName || '-',
        gatewayName,
        gatewayCount,
        nodeSubtype: sensor.nodeSubtype,
        transportType: getNodeTransportType(sensor.transportType, sensor.nodeSubtype),
        blockId: sensor.blockId,
        blockName: sensor.blockName,
        status: normalizeSensorStatus(sensor.status),
      });
    });
  });

  return sensors;
};

const buildIssueSensorRows = (row: MonitoringRow): MonitoringSensorRow[] => {
  const issueMap = new Map<string, MonitoringSensorRow>();
  const issues = [...(row.p0Issues || []), ...(row.p1Issues || [])];

  issues.forEach((issue) => {
    const status = SENSOR_ISSUE_STATUS_MAP[issue.type || ''];
    if (!status) return;

    const sensorKey = getIssueSensorKey(issue);
    if (issueMap.has(sensorKey)) return;

    issueMap.set(sensorKey, {
      id: `${row.ranchId || row.id}-${sensorKey}`,
      sensorKey,
      sensorId: issue.deviceId,
      sensorIdentifier: issue.sensorIdentifier || sensorKey,
      name: issue.deviceName || issue.sensorIdentifier || sensorKey,
      sensorType: formatLabel(issue.deviceType || 'sensor'),
      entityName: row.entityName || '-',
      ranchName: row.ranchName || '-',
      gatewayName: issue.gatewayIdentifier || '-',
      nodeSubtype: null,
      transportType: NODE_TRANSPORT_TYPE.UNKNOWN,
      blockName: null,
      status,
    });
  });

  return Array.from(issueMap.values());
};

export const getSensorStatsForRow = (row: MonitoringRow) => {
  const sensorMap = new Map<string, MonitoringSensorRow>();

  buildLoggedSensorRows(row).forEach((sensor) => sensorMap.set(sensor.sensorKey, sensor));
  buildIssueSensorRows(row).forEach((sensor) => {
    if (!sensorMap.has(sensor.sensorKey)) sensorMap.set(sensor.sensorKey, sensor);
  });

  const sensorRows = Array.from(sensorMap.values());
  return {
    sensorRows,
    totalSensors: sensorRows.length,
    onlineSensors: sensorRows.filter((sensor) => sensor.status === SENSOR_STATUS.ONLINE).length,
    degradedSensors: sensorRows.filter(
      (sensor) => sensor.status === SENSOR_STATUS.DEGRADED,
    ).length,
    offlineSensors: sensorRows.filter((sensor) => sensor.status === SENSOR_STATUS.OFFLINE).length,
  };
};

const getSummaryValue = (value: number | undefined, fallback: number) => (
  typeof value === 'number' ? value : fallback
);

export const getDeviceStatsForRow = (row: MonitoringRow) => {
  const sensorStats = getSensorStatsForRow(row);
  const gateways = row.deviceStatusLogs || [];
  const onlineGateways = gateways.filter(
    (gateway) => gateway.status !== SENSOR_STATUS.OFFLINE,
  ).length;
  const offlineGateways = gateways.filter(
    (gateway) => gateway.status === SENSOR_STATUS.OFFLINE,
  ).length;
  const summary = row.deviceSummary || {};

  return {
    sensorRows: sensorStats.sensorRows,
    totalSensors: getSummaryValue(summary.totalSensors, sensorStats.totalSensors),
    onlineSensors: getSummaryValue(summary.onlineSensors, sensorStats.onlineSensors),
    degradedSensors: getSummaryValue(summary.degradedSensors, sensorStats.degradedSensors),
    offlineSensors: getSummaryValue(summary.offlineSensors, sensorStats.offlineSensors),
    totalGateways: getSummaryValue(summary.totalGateways, gateways.length),
    onlineGateways: getSummaryValue(summary.onlineGateways, onlineGateways),
    offlineGateways: getSummaryValue(summary.offlineGateways, offlineGateways),
  };
};

const getGatewayApplicability = (
  sensors: MonitoringSensorRow[],
  totalGateways: number,
): boolean | undefined => {
  if (totalGateways > 0) return true;
  if (!sensors.length) return undefined;

  // N/A is safe only when every sensor is confirmed to use cellular transport.
  return sensors.some((sensor) => sensor.transportType !== NODE_TRANSPORT_TYPE.CELLULAR);
};

export const getPercent = (value: number, total: number) => (
  total ? (value / total) * 100 : 0
);

const getSnapshotTime = (snapshotDate?: string | Date) => {
  if (!snapshotDate) return new Date();
  // A date-only monitoring snapshot represents the complete UTC calendar day.
  if (typeof snapshotDate === 'string' && DATE_ONLY_PATTERN.test(snapshotDate)) {
    return new Date(`${snapshotDate}${END_OF_DAY_UTC_SUFFIX}`);
  }
  return new Date(snapshotDate);
};

export const getRecommendationHealth = (
  lastRecommendationTime?: string | null,
  snapshotDate?: string | Date,
) => {
  if (!lastRecommendationTime) {
    return { health: RECOMMENDATION_HEALTH.MISSING, ageHours: null };
  }

  const recommendationTime = new Date(lastRecommendationTime);
  const referenceTime = getSnapshotTime(snapshotDate);
  if (Number.isNaN(recommendationTime.getTime()) || Number.isNaN(referenceTime.getTime())) {
    return { health: RECOMMENDATION_HEALTH.MISSING, ageHours: null };
  }

  const ageHours = Math.max(
    0,
    (referenceTime.getTime() - recommendationTime.getTime()) / (60 * 60 * 1000),
  );
  const health: RecommendationHealth = ageHours > RECOMMENDATION_STALE_HOURS
    ? RECOMMENDATION_HEALTH.STALE
    : RECOMMENDATION_HEALTH.CURRENT;
  return {
    health,
    ageHours,
  };
};

export const buildSensorRows = (rows: MonitoringRow[]) => {
  const sensors = rows.reduce<MonitoringSensorRow[]>((acc, row) => (
    [...acc, ...getSensorStatsForRow(row).sensorRows]
  ), []);

  return [...sensors].sort((a, b) => {
    const statusDiff = (STATUS_ORDER[a.status] || 99) - (STATUS_ORDER[b.status] || 99);
    if (statusDiff !== 0) return statusDiff;
    return String(a.name).localeCompare(String(b.name));
  });
};

export const buildGatewayRows = (rows: MonitoringRow[]) => sortByName(
  rows.reduce<MonitoringGatewayRow[]>((gateways, row) => {
    (row.deviceStatusLogs || []).forEach((gateway, index) => {
      const gatewayKey = gateway.identifier || gateway.name || `${row.id}-${index}`;
      gateways.push({
        id: `${row.ranchId || row.id}-${gatewayKey}`,
        name: gateway.name || gateway.identifier || '-',
        entityName: row.entityName || '-',
        ranchName: row.ranchName || '-',
        connectedSensors: Object.keys(gateway.sensors || {}).length,
        connectedNodes: Object.keys(gateway.nodes || {}).length,
        status: gateway.status === SENSOR_STATUS.OFFLINE
          ? SENSOR_STATUS.OFFLINE
          : SENSOR_STATUS.ONLINE,
      });
    });
    return gateways;
  }, []),
);

export const buildBlockRows = (rows: MonitoringRow[]): MonitoringBlockRow[] => {
  const blockMap = new Map<string, MonitoringBlockRow>();

  rows.forEach((row) => {
    (row.blockRecommendationStatus || []).forEach((block) => {
      const mapKey = `${row.ranchId || row.id}-${block.blockId || block.blockName}`;
      const recommendation = block.recommendationEnabled === false
        ? { health: RECOMMENDATION_HEALTH.DISABLED, ageHours: null }
        : getRecommendationHealth(block.lastRecommendationTime, row.date);
      blockMap.set(mapKey, {
        id: mapKey,
        blockId: block.blockId,
        name: block.blockName || '-',
        ranchName: row.ranchName || '-',
        entityName: row.entityName || '-',
        lastRecommendationTime: block.lastRecommendationTime || null,
        recommendationHealth: recommendation.health,
        recommendationAgeHours: recommendation.ageHours,
        recommendationEnabled: block.recommendationEnabled,
        imageryRecommendationEnabled: block.imageryRecommendationEnabled,
        satelliteImageryEnabled: block.satelliteImageryEnabled,
      });
    });
  });

  return sortByName(Array.from(blockMap.values()));
};

const getMonitoringHealth = (
  sensors: number,
  offline: number,
  degraded: number,
  offlineGateways: number,
  staleRecommendations: number,
  missingRecommendations: number,
): MonitoringHealth => {
  if (getPercent(offline, sensors) > ENTITY_OFFLINE_CRITICAL_PERCENT) {
    return MONITORING_HEALTH.CRITICAL;
  }
  if (offline || degraded || offlineGateways || staleRecommendations || missingRecommendations) {
    return MONITORING_HEALTH.NEEDS_ATTENTION;
  }
  return MONITORING_HEALTH.ALL_GOOD;
};

export const buildRanchRows = (rows: MonitoringRow[]): MonitoringRanchRow[] => sortByName(
  rows.map((row) => {
    const stats = getDeviceStatsForRow(row);
    const blocks = buildBlockRows([row]);
    const recommendationCurrent = blocks.filter(
      (block) => block.recommendationHealth === RECOMMENDATION_HEALTH.CURRENT,
    ).length;
    const recommendationStale = blocks.filter(
      (block) => block.recommendationHealth === RECOMMENDATION_HEALTH.STALE,
    ).length;
    const recommendationMissing = blocks.filter(
      (block) => block.recommendationHealth === RECOMMENDATION_HEALTH.MISSING,
    ).length;
    return {
      id: row.ranchId || row.id,
      name: row.ranchName || '-',
      entityName: row.entityName || '-',
      blocks: blocks.length,
      sensors: stats.totalSensors,
      online: stats.onlineSensors,
      degraded: stats.degradedSensors,
      offline: stats.offlineSensors,
      gateways: stats.totalGateways,
      onlineGateways: stats.onlineGateways,
      offlineGateways: stats.offlineGateways,
      gatewayApplicable: getGatewayApplicability(stats.sensorRows, stats.totalGateways),
      recommendationCurrent,
      recommendationStale,
      recommendationMissing,
      health: getMonitoringHealth(
        stats.totalSensors,
        stats.offlineSensors,
        stats.degradedSensors,
        stats.offlineGateways,
        recommendationStale,
        recommendationMissing,
      ),
    };
  }),
);

export const buildEntityRows = (rows: MonitoringRow[]): MonitoringEntityRow[] => {
  const entityMap = new Map<string, any>();

  buildRanchRows(rows).forEach((ranch) => {
    const sourceRow = rows.find((row) => (row.ranchId || row.id) === ranch.id);
    const mapKey = String(sourceRow?.entityId || sourceRow?.entityName || ranch.entityName);
    const existing = entityMap.get(mapKey) || {
      id: mapKey,
      name: ranch.entityName,
      ranches: 0,
      blocks: 0,
      sensors: 0,
      online: 0,
      degraded: 0,
      offline: 0,
      gateways: 0,
      onlineGateways: 0,
      offlineGateways: 0,
      gatewayApplicable: undefined,
      recommendationCurrent: 0,
      recommendationStale: 0,
      recommendationMissing: 0,
    };

    existing.ranches += 1;
    existing.blocks += ranch.blocks;
    existing.sensors += ranch.sensors;
    existing.online += ranch.online;
    existing.degraded += ranch.degraded;
    existing.offline += ranch.offline;
    existing.gateways += ranch.gateways;
    existing.onlineGateways += ranch.onlineGateways;
    existing.offlineGateways += ranch.offlineGateways;
    if (ranch.gatewayApplicable === true) {
      existing.gatewayApplicable = true;
    } else if (
      ranch.gatewayApplicable === false
      && existing.gatewayApplicable === undefined
    ) {
      existing.gatewayApplicable = false;
    }
    existing.recommendationCurrent += ranch.recommendationCurrent;
    existing.recommendationStale += ranch.recommendationStale;
    existing.recommendationMissing += ranch.recommendationMissing;
    entityMap.set(mapKey, existing);
  });

  return sortByName(Array.from(entityMap.values()).map((entity) => ({
    ...entity,
    health: getMonitoringHealth(
      entity.sensors,
      entity.offline,
      entity.degraded,
      entity.offlineGateways,
      entity.recommendationStale,
      entity.recommendationMissing,
    ),
  })));
};

const getSharedCapability = <T extends keyof MonitoringRow>(rows: MonitoringRow[], key: T) => (
  rows.find((row) => row[key] !== undefined && row[key] !== null)?.[key]
);

const normalizeBlockName = (name?: string | null) => String(name || '').trim().toLowerCase();

const buildEntityDrawerRanch = (row: MonitoringRow): EntityDrawerRanch => {
  const blocks: EntityDrawerBlock[] = buildBlockRows([row]).map((block) => ({
    ...block,
    sensors: [],
  }));
  const blocksById = new Map<number, EntityDrawerBlock>();
  const blocksByName = new Map<string, EntityDrawerBlock>();

  blocks.forEach((block) => {
    if (block.blockId !== undefined) blocksById.set(block.blockId, block);
    const blockName = normalizeBlockName(block.name);
    if (blockName) blocksByName.set(blockName, block);
  });

  const unassignedSensors: MonitoringSensorRow[] = [];
  getSensorStatsForRow(row).sensorRows.forEach((sensor) => {
    const normalizedName = normalizeBlockName(sensor.blockName);
    let block = sensor.blockId !== undefined ? blocksById.get(sensor.blockId) : undefined;
    if (!block && normalizedName) block = blocksByName.get(normalizedName);

    if (!block && (sensor.blockId !== undefined || normalizedName)) {
      const blockName = sensor.blockName || `Block ${sensor.blockId}`;
      block = {
        id: `${row.ranchId || row.id}-sensor-block-${sensor.blockId || normalizedName}`,
        blockId: sensor.blockId,
        name: blockName,
        ranchName: row.ranchName || '-',
        entityName: row.entityName || '-',
        lastRecommendationTime: null,
        recommendationHealth: RECOMMENDATION_HEALTH.MISSING,
        recommendationAgeHours: null,
        sensors: [],
      };
      blocks.push(block);
      if (block.blockId !== undefined) blocksById.set(block.blockId, block);
      if (normalizedName) blocksByName.set(normalizedName, block);
    }

    if (block) block.sensors.push(sensor);
    else unassignedSensors.push(sensor);
  });

  return {
    id: row.ranchId || row.id,
    name: row.ranchName || '-',
    sensors: getDeviceStatsForRow(row).totalSensors,
    blocks: sortByName(blocks.map((block) => ({
      ...block,
      sensors: sortByName(block.sensors),
    }))),
    unassignedSensors: sortByName(unassignedSensors),
  };
};

export const buildEntityDrawerData = (
  rows: MonitoringRow[],
  entityId: string,
): EntityDrawerData | null => {
  const entityRows = rows.filter((row) => (
    String(row.entityId || row.entityName || row.id) === String(entityId)
  ));
  if (!entityRows.length) return null;

  return {
    id: entityId,
    name: entityRows[0].entityName || '-',
    subscriptionType: getSharedCapability(entityRows, 'subscriptionType') as string | null | undefined,
    irrigationRecommendationsEnabled: getSharedCapability(
      entityRows,
      'irrigationRecommendationsEnabled',
    ) as boolean | undefined,
    imageryRecommendationsEnabled: getSharedCapability(
      entityRows,
      'imageryRecommendationsEnabled',
    ) as boolean | undefined,
    satelliteImageryActive: getSharedCapability(
      entityRows,
      'satelliteImageryActive',
    ) as boolean | undefined,
    ranches: entityRows.map(buildEntityDrawerRanch).sort((a, b) => a.name.localeCompare(b.name)),
  };
};
